"use client"

import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';

// RevenueCat configuration
// These should be replaced with real keys from RevenueCat dashboard
const REVENUECAT_API_KEYS = {
  apple: process.env.NEXT_PUBLIC_REVENUECAT_APPLE_KEY || "appl_placeholder",
  google: process.env.NEXT_PUBLIC_REVENUECAT_GOOGLE_KEY || "goog_placeholder",
};

export const ENTITLEMENT_ID = "plus_membership";

/**
 * Initializes RevenueCat SDK
 */
export const initRevenueCat = async (userId?: string) => {
  if (!Capacitor.isNativePlatform()) {
    console.log("[RevenueCat] Skipping initialization on web platform");
    return;
  }

  try {
    await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
    
    const apiKey = Capacitor.getPlatform() === 'ios' 
      ? REVENUECAT_API_KEYS.apple 
      : REVENUECAT_API_KEYS.google;

    if (apiKey.includes("placeholder")) {
      console.warn("[RevenueCat] Using placeholder API key. Purchases will not work.");
    }

    await Purchases.configure({
      apiKey,
      appUserID: userId,
    });

    console.log("[RevenueCat] SDK initialized successfully" + (userId ? ` for user ${userId}` : ""));
  } catch (e) {
    console.error("[RevenueCat] Initialization failed:", e);
  }
};

/**
 * Fetches current offerings from RevenueCat
 */
export const getOfferings = async () => {
  if (!Capacitor.isNativePlatform()) return null;
  
  try {
    const offerings = await Purchases.getOfferings();
    return offerings;
  } catch (e) {
    console.error("[RevenueCat] Failed to fetch offerings:", e);
    return null;
  }
};

/**
 * Checks if user has an active entitlement
 */
export const checkSubscriptionStatus = async () => {
  if (!Capacitor.isNativePlatform()) return false;

  try {
    const customerInfo = await Purchases.getCustomerInfo();
    return customerInfo.customerInfo.entitlements.active[ENTITLEMENT_ID] !== undefined;
  } catch (e) {
    console.error("[RevenueCat] Failed to check subscription status:", e);
    return false;
  }
};

/**
 * Purchase a package
 */
export const purchasePackage = async (rcPackage: any) => {
  if (!Capacitor.isNativePlatform()) {
    throw new Error("Purchases are only supported on native platforms");
  }

  try {
    const { customerInfo } = await Purchases.purchasePackage({
      aPackage: rcPackage,
    });
    
    return {
      success: customerInfo.entitlements.active[ENTITLEMENT_ID] !== undefined,
      customerInfo,
    };
  } catch (e: any) {
    if (e.userCancelled) {
      console.log("[RevenueCat] User cancelled purchase");
      return { success: false, cancelled: true };
    }
    console.error("[RevenueCat] Purchase failed:", e);
    throw e;
  }
};

/**
 * Restore purchases
 */
export const restorePurchases = async () => {
  if (!Capacitor.isNativePlatform()) return null;

  try {
    const { customerInfo } = await Purchases.restorePurchases();
    return customerInfo;
  } catch (e) {
    console.error("[RevenueCat] Restore failed:", e);
    throw e;
  }
};

/**
 * Set user attributes for better tracking
 */
export const setUserAttributes = async (attributes: Record<string, string | null>) => {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await Purchases.setAttributes({ attributes });
  } catch (e) {
    console.error("[RevenueCat] Failed to set attributes:", e);
  }
};
